import axios from "axios";
import {apiConfig} from "@/config";

const VOUCHER_STORE_PRODUCT_ID = 92;

export default class UnlockMoreService {
    // Referral id from the invite link's URL params (?linkRefererId=...). Persisted for the
    // session so it survives the OTP → subscribe navigation. Only present when the user
    // arrived via a referral link; sent on subscribe so lucky-kite can credit the referrer.
    // No id → lucky-kite never touches Redis.
    static getLinkRefererId() {
        try {
            const fromUrl = new URLSearchParams(window.location.search).get('linkRefererId');
            if (fromUrl) {
                sessionStorage.setItem('linkRefererId', fromUrl);
                return fromUrl;
            }
            return sessionStorage.getItem('linkRefererId') || null;
        } catch (e) {
            return null;
        }
    }

    static async getUserCallerTonesList(msisdn) {
        try {
            const response = await axios.get(`${apiConfig.unlockMoreService}/v1/get-user-caller-tones-list/?msisdn=${msisdn}`, {
                    msisdn,
                },
            )
            return response.data
        } catch (e) {
            return []
        }

    }

    static async getGameLeaders(productId, lootBoxGameId, numRows) {
        return await axios.post(`${apiConfig.unlockMoreService}/v1/get-game-leaders`, {
                productId,
                lootBoxGameId,
                numRows
            },
        )
    }

    static async claimChest(msisdn) {
        const response = await axios.post(`${apiConfig.unlockMoreService}/v1/claim-chest`, {
                msisdn: msisdn,
                productId: apiConfig.productId,
            },
        )
        const row = Array.isArray(response?.data) ? (response.data[0] || {}) : (response?.data || {});
        const prize = row[''] || row.prizeText || row.PrizeText || row.prize || row.reward || '';
        if (/max chests opened/i.test(String(prize))) {
            const error = new Error('Max chests have been opened');
            error.code = 'MAX_CHESTS_OPENED';
            throw error;
        }
        return response;
    }

    static async headerEnrichmentLogin(encryptedMsisdn) {
        return await axios.get(`${apiConfig.unlockMoreService}/v1/he-login/?encryptedMsisdn=${encryptedMsisdn}`, {
                encryptedMsisdn,
            },
        )
    }

    static async getUser(msisdn) {
        return await axios.post(`${apiConfig.unlockMoreService}/v1/get-user-via-msisdn`, {
                msisdn: msisdn,
                productId: apiConfig.productId,
            },
        )
    }
    
    static async requestOtp(msisdn) {
        return await axios.post(`${apiConfig.unlockMoreService}/v1/request-otp`, {
                msisdn: msisdn,
                productId: apiConfig.productId,
            },
        )
    }

    static async validateOtp(msisdn, otp) {
        return await axios.post(`${apiConfig.unlockMoreService}/v1/validate-otp`, {
                msisdn: msisdn,
                otp,
                productId: apiConfig.productId,
            },
        )
    }

    static async trackUserLogin(msisdn) {
        return await axios.post(`${apiConfig.unlockMoreService}/v1/track-user-engagement`, {
                msisdn: msisdn,
                productId: apiConfig.productId,
                serviceId: null,
                isWeb: 1
            },
        )
    }
    
    static async upsertUserProductCounters(msisdn, streakClaimDate) {
        return await axios.post(`${apiConfig.unlockMoreService}/v1/track-user-login`, {
            msisdn,
            productId: apiConfig.productId,
            streakClaimDate,
        })
    }

    static async checkDailyStreak(msisdn) {
        return await axios.post(`${apiConfig.unlockMoreService}/v1/check-daily-streak`, {
            msisdn,
            productId: apiConfig.productId,
        })
    }

    static async claimDailyStreak(msisdn) {
        return await axios.post(`${apiConfig.unlockMoreService}/v1/claim-daily-streak`, {
            msisdn,
            productId: apiConfig.productId,
        })
    }

    static async getUserWinnings(msisdn) {
        const response = await axios.post(`${apiConfig.unlockMoreService}/v1/get-user-winnings`, {
                msisdn,
                productId: apiConfig.productId,
            },
        )
        return response.data
    }

    static getUserChest(numberOfChests) {
        const chestsArray = []
        for (let i = 0; i < numberOfChests; i++) {
            chestsArray.push({hasClaimed: false, prize: null})
        }
        return chestsArray
    }

    static async getUerBannerPriority(msisdn, category) {
        const response = await axios.post(`${apiConfig.unlockMoreService}/v1/upsell/get-upsell-services`, {
            msisdn,
            productId: apiConfig.productId,
            category
        })
        return response.data;
    }

    static async getUpsellServicesWithStatus(msisdn, category) {
        const response = await axios.post(`${apiConfig.unlockMoreService}/v1/upsell/get-upsell-services-with-status`, {
            msisdn,
            productId: apiConfig.productId,
            category
        })
        return response.data;
    }

    static async checkUserSubscription(msisdn, serviceId) {
        const response = await axios.post(`${apiConfig.unlockMoreService}/v1/upsell/check-user-subscription`, {
            msisdn,
            serviceId
        })
        return response.data;
    }

    static async doiOne(msisdn, serviceId, externalCampaignId) {
        const response = await axios.post(`${apiConfig.unlockMoreService}/v1/upsell/doi-one`, {
            msisdn,
            productId: apiConfig.productId,
            serviceId,
            externalRef: externalCampaignId,
        })
        return response.data;
    }

    static async subscribeService(msisdn, serviceId, externalRef) {
        const response = await axios.post(`${apiConfig.unlockMoreService}/v1/upsell/subscribe`, {
            msisdn,
            productId: apiConfig.productId,
            serviceId,
            externalRef: externalRef ?? null,
            linkRefererId: UnlockMoreService.getLinkRefererId(),
        });
        return response.data;
    }

    static async callerToneSubscribe(msisdn, serviceId, toneId, externalRef) {
        const response = await axios.post(`${apiConfig.unlockMoreService}/v1/upsell/caller-tone-subscribe`, {
            msisdn,
            productId: apiConfig.productId,
            serviceId,
            toneId,
            externalRef: externalRef ?? null,
            linkRefererId: UnlockMoreService.getLinkRefererId(),
        });
        return response.data;
    }

    static async adBanner(msisdn, plid) {
        console.log('PLID VALUEEEE:', plid);
        const response = await axios.get(`${apiConfig.unlockMoreService}/v1/ads/get-ads`, {
            headers: {
                    userAgent: navigator.userAgent
                },
            params: {
                msisdn,
                plid,
            }
        });
        return response.data;
    }

     static async trackAdImpression(callbackUrl) {
         const response = await axios.post(`${apiConfig.unlockMoreService}/v1/ads/track-impression`, {
             callbackUrl
         });
         return response.data;
     }


    static GAME_ACCESS_STATUSES = ['NEW', 'ACTIVE', 'GRACE', 'SUSPENDED'];

    static canPlay(statusName) {
        return UnlockMoreService.GAME_ACCESS_STATUSES.includes(
            String(statusName || '').toUpperCase()
        );
    }

    static async getUserGameState(msisdn) {
        return await axios.post(`${apiConfig.unlockMoreService}/v1/get-user-via-msisdn`, {
            msisdn,
            productId: apiConfig.productId,
        });
    }

    static async startRound(msisdn, starsSpent) {
        return await axios.post(`${apiConfig.unlockMoreService}/v1/start-round`, {
            msisdn,
            productId: apiConfig.productId,
            starsSpent,
        });
    }

    static async endRound(lootBoxGameId, msisdn, cancelledEntry, starsWon) {
        return await axios.post(`${apiConfig.unlockMoreService}/v1/end-round`, {
            lootBoxGameId,
            msisdn,
            productId: apiConfig.productId,
            cancelledEntry: !!cancelledEntry,
            starsWon,
        });
    }

    static async getUserStarsAndPlays(msisdn) {
        return await axios.post(`${apiConfig.unlockMoreService}/v1/get-user-via-msisdn`, {
            msisdn,
            productId: apiConfig.productId,
        });
    }

    static async getUserVouchers(msisdn) {
        const response = await axios.post(`${apiConfig.campaignHubService}/get-user-voucher-funds`, {
            msisdn,
            productId: VOUCHER_STORE_PRODUCT_ID,
        });
        return response.data;
    }

    static async updateUsername(msisdn, username) {
        const response = await axios.post(`${apiConfig.depService}/update-username`, {
            msisdn,
            productId: apiConfig.productId,
            username: `${username || ''}`.trim(),
        });
        const data = response?.data || {};
        if (data.usernameTaken) throw new Error('That display name is already taken.');
        return data;
    }

    static async sendReferralSms(msisdn, friendMsisdn) {
        return await axios.post(`${apiConfig.unlockMoreService}/v1/send-referral-sms`, {
            msisdn,
            productId: apiConfig.productId,
            friendMsisdn,
        });
    }
}
